import { Box, Grid, Switch, Typography } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import SettingsIcon from "@material-ui/icons/Settings";
import VisibleFor from "auth/components/VisibleFor";
import { Role } from "auth/types";
import { AlertDialog } from "common/components/AlertDialog";
import { ButtonCard } from "common/components/ButtonCard";
import { TwoFactorStepper } from "meter/components/2Factor/twoFactorStepper";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { createSelector } from "reselect";
import {
  getAllSensorDataForSingleNode,
  toggleAllSensorsAction,
  toggleSingleSensorAction,
} from "safetySensors/Sensors.actions";
import { ISensorData } from "safetySensors/types";
import { DeviceType } from "../../common/types";
import { IApplicationState } from "../../redux/root-reducer";
import SafetySensorButton from "../../safetySensors/components/safetySensorStatusbutton";
import styles from "./dashboardForSensors.module.scss";

const getSensorsForNodeSelector = createSelector(
  (state: IApplicationState) => state.sensors,
  (sensorState) => ({
    sensors: sensorState.allSensorDataForSingleNode.filter(
      (s) =>
        s.DeviceType === DeviceType.Leakage || s.DeviceType === DeviceType.Smoke
    ),
  })
);

export const DashboardForSensors = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const [chosenSensor, setChosenSensor] = useState<ISensorData>();
  const [isChecked, setIsChecked] = useState(false);
  const [activateAll, setActivateAll] = useState(false);
  const { sensors } = useSelector(getSensorsForNodeSelector);
  const { PhoneNumbers } = useSelector(
    (state: IApplicationState) => state.common.currentUserInfo
  );
  const { selectedNode } = useSelector(
    (state: IApplicationState) => state.node
  );
  const { monitoringOn } = useSelector(
    (state: IApplicationState) => state.sensors
  );
  const { id }: { id: string } = useParams();

  const domainCode =
    useSelector(
      (state: IApplicationState) => state.common.selectedDomain?.Code
    ) ?? "";

  const shouldOpenAlert = sensors.some((s) => s.Alert);

  const handleRefresh = () => {
    dispatch(getAllSensorDataForSingleNode(domainCode, parseInt(id) ?? ""));
  };

  const toggleAll = (checked: boolean) => {
    if (!PhoneNumbers || PhoneNumbers.length < 1) {
      setTwoFactorOpen(true);
      return;
    }

    if (!checked) {
      dispatch(toggleAllSensorsAction(domainCode, checked));
      return;
    } else if (checked && shouldOpenAlert) {
      setAlertDialogOpen(true);
      setActivateAll(true);
      setIsChecked(true);
    } else {
      dispatch(toggleAllSensorsAction(domainCode, checked));
    }
  };

  const toggleSingle = (checked: boolean, sensor: ISensorData) => {
    if (!PhoneNumbers || !PhoneNumbers.length) {
      setTwoFactorOpen(true);
      return;
    }

    if (checked && sensors.filter((s) => s.Alert).length >= 1) {
      setAlertDialogOpen(true);
      setChosenSensor(sensor);
      setIsChecked(checked);
      return;
    }
    dispatch(toggleSingleSensorAction(domainCode, sensor, checked));
  };

  const alertClose = (shouldActivate: boolean) => {
    if (shouldActivate) {
      !activateAll
        ? dispatch(
            toggleSingleSensorAction(domainCode, chosenSensor!, isChecked)
          )
        : dispatch(toggleAllSensorsAction(domainCode, isChecked));
    }
    setAlertDialogOpen(false);
  };

  const handleTwoFactorClose = (success?: boolean) => {
    setTwoFactorOpen(false);
    if (!success) return;

    shouldOpenAlert
      ? setAlertDialogOpen(true)
      : dispatch(toggleAllSensorsAction(domainCode, true));
  };

  return (
    <Box className={styles.container} display="flex" gridGap={15}>
      <VisibleFor
        roles={[Role.Tenant]}
        child={
          <ButtonCard fullWidth>
            <Typography variant="h4">{t("Activate safety")}</Typography>
            <Switch
              checked={!!monitoringOn}
              size="small"
              color="secondary"
              onChange={(e) => toggleAll(e.target.checked)}
            />
          </ButtonCard>
        }
      />

      <VisibleFor
        roles={[Role.Tenant]}
        child={
          <ButtonCard
            flexGrow={1}
            onClick={() =>
              history.push(
                `${
                  domainCode ? `/${domainCode}` : ""
                }/profile/manage-notifications`
              )
            }
          >
            <Typography variant="h4">{t("Manage notifications")}</Typography>
            <SettingsIcon style={{ height: 24 }} />
          </ButtonCard>
        }
      />
      <ButtonCard flexGrow={0} onClick={handleRefresh}>
        <Typography variant="h4">{t("Update")}</Typography>
        <RefreshIcon />
      </ButtonCard>
      {sensors.length < 1 ? (
        <span>{t("There are no sensors in the apartment")}</span>
      ) : (
        <Grid container spacing={1}>
          {sensors
            .sort((a, b) => a.MeasuringPointID - b.MeasuringPointID)
            .map((sensor, i) => (
              <Grid item xs={6} md={4} sm={4} key={i}>
                <SafetySensorButton
                  selectedNode={selectedNode}
                  sensor={sensor}
                  key={sensor.ExternalKey}
                  onToggle={(checked) => toggleSingle(checked, sensor)}
                />
              </Grid>
            ))}
        </Grid>
      )}
      <TwoFactorStepper open={twoFactorOpen} onClose={handleTwoFactorClose} />
      <AlertDialog
        open={alertDialogOpen}
        onClose={alertClose}
        activateTitle={t("Activate safety")}
        openNodes={sensors
          .filter((s) => s.Alert)
          .map((s) => `${s.MeasuringPointName}: ${s.Alert?.Description || ""}`)}
      />
    </Box>
  );
};
